import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import {
  useParams,
  useAsyncData,
  FetchGridParameters,
  Stack,
  Box,
  Drawer,
} from 'shared';
import {
  useApi,
  DEFAULT_PAGE_PARAMS,
  PageState,
  PagePresenter,
  useBreakpoints,
  SensorHeader,
  MobileIconButton,
} from 'App';
import {
  SensorReadingsCard,
  SensorEventLogCard,
  CalculationTooltip,
} from 'Sensors';
import { SentryApiClient } from '_generated/api';
import { SensorStateIcon } from 'Sensors/components/SensorStateIcon';
import { useBreadcrumbs } from 'App/hooks/BreadcrumbsProvider';
import { useSensorRanges } from 'Sensors/hooks/useSensorRanges';
import styles from './SensorDashboard.module.css';
import {
  CollapsibleCard,
  HorizontalCollapsibleCard,
} from 'App/components/CollapsibleCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { SensorStateTable } from 'SensorStates';
import { redAndAmberFilters } from 'App/utils/redAndAmberFilters';
import { ChartClickerProvider, ChartPoint } from 'SensorReadings';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { SensorEdit } from '../SensorEdit';
import { SiteThemeProvider } from 'Sites/hooks';
import { SiteThemeIcon } from 'Sites/components/SiteThemeIcon';
import { getThemeConfig } from 'Sites/themes';
import {
  isSiteSensorBatchArray,
  useComparableSensors,
} from '../../hooks/useComparableSensors';

export interface SensorDashboardProps {}

const now = new Date();
const oneDayAgo = moment(now).subtract(1, 'day').toDate();
const oneWeekAgo = moment(now).subtract(1, 'week').toDate();

const SENSOR_STATE_TABLE_WIDTH = 660;

export const SensorDashboard: React.FC<SensorDashboardProps> = ({}) => {
  // TODO: pull out common code between this and SensorDetails
  const { sensorApi, siteApi, sensorStateApi, eventLogApi } = useApi();
  const params = useParams<{ sensorId?: string }>();
  const sensorId = (params.sensorId && parseInt(params.sensorId)) || -1;
  const {
    data: sensorDetails,
    isLoading: isLoadingSensor,
    error: sensorError,
    executeRequest: reloadSensors,
  } = useAsyncData(async () => sensorApi.getById(sensorId), [
    sensorId,
    sensorApi,
  ]);

  const {
    data: sensorPermissions,
    isLoading: permissionsLoading,
  } = useAsyncData(
    async () => (sensorId ? sensorApi.getUserPermissions(sensorId) : undefined),
    [sensorApi, sensorId]
  );

  const [clickedPoint, setClickedPoint] = useState<ChartPoint>();

  const { setBreadcrumbs } = useBreadcrumbs();
  //For our breadcrumbs, we need the site name, but unfortunately, the sensor
  // details only has the siteId. Since it would be a lot of work to append
  // a SiteDTO to the SensorDetailsDTO, I'm just going to fetch it manually
  // for now
  const {
    data: sensorSite,
    isLoading: sensorSiteLoading,
  } = useAsyncData(
    async () =>
      sensorDetails?.siteId ? siteApi.getById(sensorDetails.siteId) : undefined,
    [sensorDetails, siteApi]
  );
  useEffect(() => {
    setBreadcrumbs([
      {
        display: sensorSite?.name,
        linkTo: `/dashboard/site/${sensorSite?.id}`,
        loading: sensorSiteLoading,
      },
      {
        display: sensorDetails?.sensorName,
        linkTo: `/dashboard/sensor/${sensorDetails?.id}`,
      },
    ]);
  }, [sensorDetails, sensorSiteLoading, sensorSite, setBreadcrumbs]);

  const [showEdit, setShowEdit] = useState(false);

  const {
    primaryRange,
    comparisons,
    timezone,
    updateTimezone,
    readings,
    setRanges,
    refreshEventLogs: reloadChartEventLogs,
    flipYAxis,
    focusOnTime,
  } = useSensorRanges(sensorDetails, sensorSite);

  const [eventLogParams, setEventLogParams] = useState<
    FetchGridParameters<SentryApiClient.EventLogDTO>
  >(DEFAULT_PAGE_PARAMS);
  const {
    data: eventLogsData,
    isLoading: loadingEventLogs,
    error: eventLogsError,
    executeRequest: reloadEventLogsTable,
  } = useAsyncData(
    () =>
      sensorApi.getEventLogs(
        sensorId,
        eventLogParams.pageSize,
        eventLogParams.page,
        SentryApiClient.EventLogFilters.Timestamp,
        SentryApiClient.OrderDirection.Descending
      ),
    [sensorId, eventLogParams, sensorApi]
  );

  const firstEventLogId =
    eventLogsData?.items && eventLogsData?.items.length !== 0
      ? eventLogsData?.items[0].id!
      : 0;
  const {
    data: eventLogPermissions,
    isLoading: eventLogPermissionsLoading,
  } = useAsyncData(
    async () =>
      loadingEventLogs
        ? undefined
        : eventLogApi.getUserPermissions(firstEventLogId, sensorId),
    [loadingEventLogs, eventLogApi, firstEventLogId, sensorId]
  );

  const reloadEventLogs = useCallback(() => {
    reloadChartEventLogs();
    reloadEventLogsTable();
  }, [reloadEventLogsTable, reloadChartEventLogs]);

  const { smd, xxl } = useBreakpoints();
  const [showStates, setShowStates] = useState(false);
  const [sensorStatesPageParams, setSensorStatesPageParams] = useState<
    FetchGridParameters<SentryApiClient.SensorStateDTO>
  >(DEFAULT_PAGE_PARAMS);
  const {
    data: sensorStatesData,
    isLoading: loadingSensorStates,
    error: sensorStatesError,
  } = useAsyncData(
    async () =>
      sensorDetails && sensorDetails.id
        ? sensorStateApi.get(
            sensorStatesPageParams.pageSize,
            sensorStatesPageParams.page,
            SentryApiClient.SensorStateFilters.StartTime,
            SentryApiClient.OrderDirection.Descending,
            [
              {
                field: SentryApiClient.SensorStateFilters.SensorId,
                operator: SentryApiClient.Operator.Equals,
                value: sensorDetails.id.toString(),
              },
            ]
          )
        : undefined,
    [sensorDetails, sensorStatesPageParams, sensorStateApi]
  );

  const {
    data: comparableSensors,
    isLoading: comparableSensorsLoading,
  } = useComparableSensors(sensorDetails);

  const [editError, setEditError] = useState<Error>();
  const getPageState = () => {
    if (isLoadingSensor) {
      return PageState.Loading;
    } else if (sensorError || editError || eventLogsError) {
      return PageState.Error;
    }

    return PageState.Loaded;
  };
  const eventLogsCardNode = (
    <SensorEventLogCard
      sensorDetails={sensorDetails}
      sensorId={sensorId}
      eventLogs={eventLogsData?.items}
      totalEventLogs={eventLogsData?.totalItems}
      eventLogParams={eventLogParams}
      permissionsLoading={eventLogPermissionsLoading}
      timezone={timezone ?? moment.tz.guess()}
      loadingEventLogs={loadingEventLogs}
      addableSensors={
        isSiteSensorBatchArray(comparableSensors)
          ? comparableSensors.flatMap((batch) => batch.sensors.items ?? [])
          : comparableSensors ?? []
      }
      createEventLogTimestamp={clickedPoint?.date}
      canCreate={eventLogPermissions?.canCreate}
      onError={setEditError}
      onUpdate={reloadEventLogs}
      onEventLogParamsChange={setEventLogParams}
      onClose={() => setClickedPoint(undefined)}
      onShowOnChartClicked={focusOnTime}
    />
  );

  const statusTableNode = (
    <SensorStateTable
      sensorStates={sensorStatesData?.items}
      total={sensorStatesData?.totalItems}
      isLoading={loadingSensorStates}
      params={sensorStatesPageParams}
      onChange={setSensorStatesPageParams}
      timezone={timezone ?? moment.tz.guess()}
      height={500}
    />
  );

  // Create themed title with product name and icon
  const sensorTitle = sensorDetails && (
    <Stack gap="sm" alignment="start">
      <SensorHeader
        prepend={
          sensorSite?.uiTheme != null &&
          sensorSite.uiTheme !== SentryApiClient.UIThemeType.Default &&
          getThemeConfig(sensorSite.uiTheme) && (
            <>
              <SiteThemeIcon theme={sensorSite.uiTheme} small />
              <span style={{ fontWeight: 600, fontSize: '14px' }}>
                {getThemeConfig(sensorSite.uiTheme)?.productName}
              </span>
            </>
          )
        }
        sensorState={sensorDetails.stateType}
        calculation={sensorDetails.calculation}
        sensorName={sensorDetails.sensorName}
        sensorDescription={sensorDetails.description}
        tSenSActive={sensorDetails.tSenSActive}
      />
    </Stack>
  );

  return (
    <>
      <PagePresenter pageState={getPageState()}>
        <Stack direction="vertical" gap={20} stretch>
          {sensorDetails && primaryRange && (
            <ChartClickerProvider
              point={clickedPoint}
              onClick={(point) =>
                eventLogPermissions?.canCreate
                  ? setClickedPoint(point)
                  : undefined
              }
            >
              <Stack alignment="end" gap={8}>
                {permissionsLoading && <LoadingOutlined />}
                {sensorPermissions?.canUpdate && (
                  <MobileIconButton
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setShowEdit(true)}
                  >
                    Edit
                  </MobileIconButton>
                )}
              </Stack>
              <SensorReadingsCard
                title={sensorTitle}
                startDate={primaryRange?.from ?? oneWeekAgo}
                endDate={primaryRange?.to ?? now}
                targetRange={{ sensor: sensorDetails, ...primaryRange }}
                comparisons={comparisons}
                comparableSensors={comparableSensors}
                comparableSensorsLoading={comparableSensorsLoading}
                readings={readings}
                onComparison={setRanges}
                timezone={timezone}
                enableImport={sensorPermissions?.canUpdate}
                onTimezoneChange={updateTimezone}
                onFlipAxis={flipYAxis}
              />
            </ChartClickerProvider>
          )}
          {xxl && (
            <Stack
              direction="horizontal"
              alignment="middle"
              stretch
              fill
              gap="md"
              style={{ overflowX: 'hidden' }}
            >
              {/* Unfortunately, ant tables do not play nicely with pure flex. Rather than
            shrinking and growing like any normal div, ant tables only grow and then
            refuse to shrink in a flex container. To make this work, we need to be explicit
            with our table widths */}
              <div
                style={{
                  width: `calc(100% - ${
                    showStates ? SENSOR_STATE_TABLE_WIDTH + 60 : 32
                  }px)`,
                  transition: '0.4s',
                }}
              >
                {eventLogsCardNode}
              </div>
              <HorizontalCollapsibleCard
                openWidth={SENSOR_STATE_TABLE_WIDTH}
                sider={
                  <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
                }
                siderTooltip="Sensor State History"
                openLeft
                isOpen={showStates}
                onChange={setShowStates}
                style={{ height: '100%' }}
              >
                {statusTableNode}
              </HorizontalCollapsibleCard>
            </Stack>
          )}
          {!xxl && (
            <>
              {eventLogsCardNode}
              <CollapsibleCard title="Sensor State History" defaultHidden>
                {statusTableNode}
              </CollapsibleCard>
            </>
          )}
        </Stack>
      </PagePresenter>
      <Drawer
        open={showEdit}
        onClose={() => setShowEdit(false)}
        title="Edit Sensor"
        width={smd ? 400 : 300}
      >
        {sensorDetails && (
          <SensorEdit
            sensor={sensorDetails}
            onEdit={() => {
              setShowEdit(false);
              reloadSensors();
            }}
            onError={setEditError}
          />
        )}
      </Drawer>
    </>
  );
};
